import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  FaUsers,
  FaDollarSign,
  FaProjectDiagram,
  FaMicrophone,
} from "react-icons/fa";

const ImpactSection = () => {
  const [count1, setCount1] = useState(0);
  const [count2, setCount2] = useState(0); // ForgeRock amount
  const [count3, setCount3] = useState(0);
  const [count4, setCount4] = useState(0);

  useEffect(() => {
    const target1 = 50; // Industry testimonials
    const target2 = 500000; // $ saved for ForgeRock
    const target3 = 100; // Projects impacted
    const target4 = 30; // Talks, workshops, and mentoring sessions

    const incrementCounter = (
      count,
      setCount,
      target,
      interval = 100,
      stepFactor = 200,
    ) => {
      const step = Math.ceil(target / stepFactor);
      if (count < target) {
        const timer = setInterval(() => {
          setCount((prevCount) => {
            if (prevCount + step >= target) {
              clearInterval(timer);
              return target;
            }
            return prevCount + step;
          });
        }, interval);
      }
    };

    // Function to start counters only when in view
    const startCounters = () => {
      incrementCounter(count1, setCount1, target1, 200, 250); // Slower increments
      incrementCounter(count2, setCount2, target2, 50, 300); // Moderate increments
      incrementCounter(count3, setCount3, target3, 200, 250); // Slower increments
      incrementCounter(count4, setCount4, target4, 200, 250); // Slower increments
    };

    // Use IntersectionObserver to start counters when the section is in view
    let observer;
    const section = document.getElementById("impact-section");
    if (section) {
      observer = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting) {
            startCounters();
            observer.disconnect(); // Disconnect after starting the counters
          }
        },
        { threshold: 0.3 }, // Adjust threshold as needed
      );
      observer.observe(section);
    }

    return () => {
      if (observer) observer.disconnect();
    };
  }, [count1, count2, count3, count4]);

  const impactItems = [
    {
      icon: <FaUsers className="text-4xl mb-4 text-blue-400" />,
      count: count1,
      suffix: "+",
      label: "Industry Leaders' Testimonials",
      color: "from-blue-500/20 to-blue-600/20",
    },
    {
      icon: <FaDollarSign className="text-4xl mb-4 text-green-400" />,
      count: count2.toLocaleString(),
      prefix: "$",
      suffix: "+",
      label: "Saved for Clients",
      color: "from-green-500/20 to-green-600/20",
    },
    {
      icon: <FaProjectDiagram className="text-4xl mb-4 text-purple-400" />,
      count: count3,
      suffix: "+",
      label: "Projects Secured Globally",
      color: "from-purple-500/20 to-purple-600/20",
    },
    {
      icon: <FaMicrophone className="text-4xl mb-4 text-orange-400" />,
      count: count4,
      suffix: "+",
      label: "Talks & Workshops Delivered",
      color: "from-orange-500/20 to-orange-600/20",
    },
  ];

  return (
    <motion.section
      id="impact-section"
      className="py-20 bg-gradient-to-b from-gray-900 via-gray-800 to-gray-900"
      initial={{ opacity: 0 }}
      whileInView={{ opacity: 1 }}
      transition={{ duration: 0.8 }}
      viewport={{ once: true }}
    >
      <div className="container mx-auto px-4">
        <motion.h2
          initial={{ opacity: 0, y: -20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-4xl md:text-5xl font-bold text-center mb-12 bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent"
        >
          My Professional Impact
        </motion.h2>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {impactItems.map((item, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
              whileHover={{ scale: 1.05 }}
              className="relative group"
            >
              <div
                className={`bg-gradient-to-br ${item.color} backdrop-blur-lg border border-white/10 rounded-2xl p-8 text-center hover:bg-white/10 transition-all duration-300 h-full`}
              >
                {/* Icon */}
                <div className="flex justify-center">{item.icon}</div>

                {/* Counter */}
                <h3 className="text-4xl md:text-5xl font-bold text-white mb-2">
                  {item.prefix}
                  {item.count}
                  {item.suffix}
                </h3>

                {/* Label */}
                <p className="text-gray-300 text-sm md:text-base">
                  {item.label}
                </p>

                {/* Hover effect */}
                <div className="absolute inset-0 bg-gradient-to-br from-white/0 to-white/5 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </motion.section>
  );
};

export default ImpactSection;
